import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { MapPin, Confetti, BookOpen, Question, Warning, EnvelopeSimple, Martini, PaperPlaneTilt, Camera, Megaphone, PlayCircle } from "@phosphor-icons/react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import TeamSection from "@/components/TeamSection";
import QuizCTACard from "@/components/QuizCTACard";
import BingoCTACard from "@/components/BingoCTACard";
import FeedbackCTACard from "@/components/FeedbackCTACard";
import { isConferenceOver } from "@/lib/conference";
import { VIRTUAL_PLATFORM } from "@/lib/virtual";

export default function Info() {
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get("/info").then((r) => setInfo(r.data)); }, []);
  if (!info) return <div className="py-10 text-center text-[#7A828C]">Loading…</div>;

  const Section = ({ icon: Icon, title, children, testid }) => (
    <section data-testid={testid} className="p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} className="text-[#000000]" weight="duotone" />
        <h2 className="font-display text-lg">{title}</h2>
      </div>
      <div className="text-sm text-[#5a5a5a] leading-relaxed">{children}</div>
    </section>
  );

  // Once the conference is over we hide the in-session game CTAs (Quiz and
  // Bingo). The Feedback CTA stays as long as Admin keeps feedback published
  // so delegates can still submit it on the journey home.
  const showGameCTAs = !isConferenceOver();

  return (
    <div data-testid="info-page" className="space-y-4">
      <div className="mb-2">
        <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest mb-1">Practical info</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Get your bearings</h1>
      </div>

      {showGameCTAs && <QuizCTACard />}
      {showGameCTAs && <BingoCTACard />}
      <FeedbackCTACard />

      <Link
        to="/announcements"
        data-testid="info-announcements-link"
        className="flex items-center gap-3 p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm hover:border-[#000000] transition-colors"
      >
        <Megaphone size={22} weight="duotone" className="text-[#000000] flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-base">News &amp; announcements</div>
          <div className="text-xs text-[#7A828C]">Room changes, schedule tweaks, organiser updates</div>
        </div>
        <span className="font-mono-time text-xs uppercase tracking-widest text-[#000000]">Open →</span>
      </Link>

      <a
        href={VIRTUAL_PLATFORM.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="info-virtual-platform-link"
        className="flex items-center gap-3 p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm hover:border-[#000000] transition-colors"
      >
        <PlayCircle size={22} weight="duotone" className="text-[#DC4F48] flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-base">Replays · DFTB26 Virtual Platform</div>
          <div className="text-xs text-[#7A828C] truncate">{VIRTUAL_PLATFORM.headline} · {VIRTUAL_PLATFORM.subline.toLowerCase()}</div>
        </div>
        <span className="font-mono-time text-xs uppercase tracking-widest text-[#000000]">Open →</span>
      </a>

      <a
        href="https://pov.camera/qr/AwJ00ulRjvsHIvxc35py"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="info-pov-camera-link"
        className="flex items-center gap-3 p-4 bg-[#000000] text-[#FFFFFF] rounded-sm hover:bg-[#1c1c24] transition-colors"
      >
        <Camera size={22} weight="duotone" className="text-[#43C4E3] flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-base">Share your conference photos</div>
          <div className="text-xs text-[#43C4E3]/90">Upload to the shared DFTB26 album on POV Camera</div>
        </div>
        <span className="font-mono-time text-xs uppercase tracking-widest text-[#43C4E3]">Open →</span>
      </a>

      <Section icon={MapPin} title="Venue" testid="info-venue">
        <p className="font-medium text-[#1c1c24]">{info.venue_name}</p>
        <p>{info.venue_address}</p>
        {info.venue_map_url && (
          <img src={info.venue_map_url} alt="Venue map" className="w-full rounded-sm border border-[#E5E9F0] mt-3" />
        )}
      </Section>

      <TeamSection />

      <Section icon={Martini} title="Welcome reception" testid="info-reception">
        <p className="whitespace-pre-line">{info.welcome_reception}</p>
      </Section>
      <Section icon={Confetti} title="Conference party" testid="info-party">
        <p className="whitespace-pre-line">{info.conference_party}</p>
      </Section>
      <Section icon={BookOpen} title="Storytelling evening" testid="info-storytelling">
        <p className="whitespace-pre-line">{info.storytelling_evening}</p>
      </Section>
      <Section icon={BookOpen} title="Dress code" testid="info-dress">
        <p className="whitespace-pre-line">{info.dress_code}</p>
      </Section>
      <Section icon={BookOpen} title="Dietary info" testid="info-dietary">
        <p className="whitespace-pre-line">{info.dietary_info}</p>
      </Section>
      <Section icon={Warning} title="Code of conduct" testid="info-coc">
        <p className="whitespace-pre-line">{info.code_of_conduct}</p>
      </Section>

      <Section icon={Question} title="FAQs" testid="info-faqs">
        <Accordion type="single" collapsible className="mt-1">
          {(info.faqs || []).map((f, i) => (
            <AccordionItem key={f.q || `faq-${i}`} value={`faq-${i}`} className="border-b-[#E5E9F0]">
              <AccordionTrigger className="text-sm text-[#1c1c24] hover:no-underline py-3">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-[#5a5a5a] whitespace-pre-line">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      <Section icon={EnvelopeSimple} title="Contact" testid="info-contact">
        <a href={`mailto:${info.contact_email}`} className="text-[#000000] hover:underline">{info.contact_email}</a>
      </Section>

      <Link
        to="/info/networking"
        data-testid="info-networking-link"
        className="flex items-center gap-3 p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm hover:border-[#000000] transition-colors"
      >
        <PaperPlaneTilt size={20} weight="duotone" className="text-[#000000] -rotate-[12deg] flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-display text-base">How networking works</div>
          <div className="text-xs text-[#7A828C]">Opt-in only. What other delegates see. How to turn it off.</div>
        </div>
        <span className="font-mono-time text-xs uppercase tracking-widest text-[#000000]">Read →</span>
      </Link>

      <div data-testid="info-legal" className="pt-2 pb-4 flex items-center justify-center gap-4 text-xs font-mono-time uppercase tracking-widest text-[#7A828C]">
        <Link to="/privacy" data-testid="info-privacy-link" className="hover:text-[#000000]">Privacy</Link>
        <span aria-hidden="true">·</span>
        <Link to="/terms" data-testid="info-terms-link" className="hover:text-[#000000]">Terms</Link>
      </div>
    </div>
  );
}
